import { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

import Axios from 'src/libs/axios';
import history from 'src/libs/history';
import LocalStorage from 'src/utils/LocalStorage';
import { HTTP_CODE, PATH, PLATFORM } from 'src/constants/app';
import { store } from 'src/store';

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

export default class HTTPRequest extends Axios {
  public constructor(
    config?: AxiosRequestConfig,
    options: { redirectErrors?: boolean } = {},
  ) {
    super(config);

    this.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        const accessToken = LocalStorage.accessToken;

        if (accessToken) {
          const headers = {
            Authorization: `Bearer ${accessToken}`,
          };

          config.headers = Object.assign(config.headers, headers);
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    this.interceptors.response.use(
      (response: AxiosResponse<any>) => {
        return response;
      },
      (error: AxiosError) => {
        if (options.redirectErrors !== false) {
          store.dispatch({ type: 'app/updateError', payload: error });
        }

        const statusCode = error.response && error.response.status;
        const originalRequest = error.config;

        switch (statusCode) {
          case HTTP_CODE.UNAUTHORIZED:
            if (
              error.response?.data.message === 'Unauthorized.' &&
              // @ts-ignore
              !originalRequest._retry
            ) {
              if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                  failedQueue.push({ resolve, reject });
                })
                  .then((token) => {
                    LocalStorage.setToken(token);
                    const newHeader = Object.assign(originalRequest.headers, {
                      // @ts-ignore
                      Authorization: `Bearer ${token.access_token}`,
                    });

                    originalRequest.headers = newHeader;
                    this.instance.defaults.headers.common = newHeader;
                    return this.instance(originalRequest);
                  })
                  .catch((err) => {
                    return Promise.reject(err);
                  });
              }

              // @ts-ignore
              originalRequest._retry = true;
              isRefreshing = true;

              return new Promise((resolve, reject) => {
                new Axios().instance
                  .post('auth/token/refresh', {
                    refresh_token: LocalStorage.refreshToken,
                    platform: PLATFORM.JENO,
                  })
                  .then(({ data }) => {
                    LocalStorage.setToken(data.data.token);

                    const newHeaders = Object.assign(originalRequest.headers, {
                      Authorization: `Bearer ${data.data.token.access_token}`,
                    });
                    originalRequest.headers = newHeaders;
                    this.instance.defaults.headers.common = newHeaders;

                    processQueue(null, data.data.token);

                    resolve(this.instance(originalRequest));
                  })
                  .catch((err) => {
                    processQueue(err, null);
                    if (
                      err.response.config.url === 'auth/token/refresh' &&
                      err.response.data.message ===
                        'Sorry, your refresh token is invalid.'
                    ) {
                      LocalStorage.removeInfo();
                      history.push(PATH.LOGIN);
                    }
                    reject(err);
                  })
                  .finally(() => {
                    isRefreshing = false;
                  });
              });
            }

            if (history.location.pathname !== PATH.LOGIN) {
              LocalStorage.removeInfo();
              history.push(PATH.LOGIN);
            }
            break;
          case HTTP_CODE.FORBIDDEN:
            if (options.redirectErrors !== false) history.push(PATH.FORBIDDEN);
            break;
          case HTTP_CODE.NOT_FOUND:
            if (options.redirectErrors !== false) history.push(PATH.NOT_FOUND);
            break;
          case HTTP_CODE.INTERNAL_SERVER_ERROR:
            if (options.redirectErrors !== false)
              history.push(PATH.INTERNAL_SERVER_ERROR);
            break;
          default:
            break;
        }
        return Promise.reject(error);
      },
    );
  }

  public get<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.get(url, { data: {}, ...config });
  }

  public post<T = any, B = any>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.post(url, data, config);
  }

  public put<T = any, B = any>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.put(url, data, config);
  }

  public delete<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.delete(url, { data: {}, ...config });
  }

  public patch<T = any, B = any>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.patch(url, data, config);
  }
}
